import { useState, useEffect, useMemo } from "react";
import { getGeographicDistributionReq } from "@/api/projectsApi";
import { GeographicDistributionItem } from "@/types/projects";
import * as Sentry from "@sentry/react";

/**
 * Custom hook for fetching and filtering geographic distribution data
 * Note: Currently uses client-side filtering via useMemo. The API supports
 * server-side search, but we fetch all data once for better UX (instant filtering).
 * For large datasets, consider switching to server-side search by passing
 * search to getGeographicDistributionReq().
 *
 * @param search - Search term to filter cities (client-side filtering)
 * @returns Object containing loading state, error, raw data, and filtered data
 */
export const useGeographicData = (search: string) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [geoData, setGeoData] = useState<GeographicDistributionItem[]>([]);

  useEffect(() => {
    const abortController = new AbortController();

    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        const response = await getGeographicDistributionReq(
          "",
          abortController.signal
        );
        const data = response.data.geographicDistribution || [];

        setGeoData(data);
      } catch (err: any) {
        // Ignore abort errors (component unmounted or request cancelled)
        if (err.name === "AbortError" || err.code === "ERR_CANCELED") {
          return;
        }

        console.error("Failed to fetch geographic distribution:", err);
        Sentry.captureException(err, {
          tags: { feature: "geographic-distribution" },
        });

        const errorMessage =
          err instanceof Error
            ? `Failed to load data: ${err.message}`
            : "Failed to load geographic distribution data";
        setError(errorMessage);
      } finally {
        setLoading(false);
      }
    };

    fetchData();

    return () => {
      abortController.abort();
    };
  }, []);

  const filteredData = useMemo(() => {
    if (!search.trim()) return geoData;

    const searchLower = search.toLowerCase().trim();
    return geoData.filter((item) =>
      item.city.toLowerCase().includes(searchLower)
    );
  }, [geoData, search]);

  return { loading, error, geoData, filteredData };
};
